"use client"

// A thin, dependency-free stand-in for `vrXRStore` (the real one lives in ./vr-player, which
// imports three.js + @react-three/fiber + @react-three/xr — about 430 KB gzipped).
//
// Importing ./vr-player statically from the booru detail modal put all of that into the home
// page's first-load JavaScript (measured on a production build: three chunks containing
// three.js in the 37-chunk, 1.3 MB-gzip first load), for a feature most visitors never open.
// This wrapper keeps the same call sites working (`vrXRStore.getState().session`, `.subscribe`,
// `.enterVR`, `.enterAR`) but only loads the real module when VR is actually opened — the
// <VRPlayer> dynamic import in vr-overlay.tsx, or enterVR()/enterAR() themselves.

type RealStore = typeof import("./vr-player")["vrXRStore"]
type RealState = ReturnType<RealStore["getState"]>
type Listener = (state: RealState, prev: RealState) => void

let real: RealStore | null = null
let pending: Promise<typeof import("./vr-player")> | null = null
// Subscriptions made before the real store exists; attached the moment it loads. Nothing here
// triggers the load — only opening VR does.
const queued = new Set<{ listener: Listener; unsubscribe?: () => void; cancelled: boolean }>()

export function loadVRPlayerModule() {
  if (!pending) {
    pending = import("./vr-player").then((mod) => {
      real = mod.vrXRStore
      queued.forEach((entry) => {
        if (!entry.cancelled) entry.unsubscribe = real!.subscribe(entry.listener)
      })
      queued.clear()
      return mod
    })
    // Let a failed chunk load be retried on the next attempt.
    pending.catch(() => {
      pending = null
    })
  }
  return pending
}

export const vrXRStore = {
  /** Before the module loads there can't be a session, so report "none". */
  getState: (): Pick<RealState, "session"> & Partial<RealState> =>
    real ? real.getState() : { session: undefined },

  subscribe(listener: Listener): () => void {
    if (real) return real.subscribe(listener)
    const entry: { listener: Listener; unsubscribe?: () => void; cancelled: boolean } = {
      listener,
      cancelled: false,
    }
    queued.add(entry)
    return () => {
      entry.cancelled = true
      queued.delete(entry)
      entry.unsubscribe?.()
    }
  },

  async enterVR() {
    const mod = await loadVRPlayerModule()
    return mod.vrXRStore.enterVR()
  },

  async enterAR() {
    const mod = await loadVRPlayerModule()
    return mod.vrXRStore.enterAR()
  },
}
